import React, { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Button, Dialog, DialogPanel, DialogTitle, Transition, TransitionChild } from '@headlessui/react';
import { X, Search, User, Building2 } from 'lucide-react';
import { Formik, Form, FormikProps } from 'formik';
import { ModalFooter } from 'components/common/ModalFooter';
import FormInput from 'components/common/FormInput';
import { CustomSelect } from 'components/common/CustomSelect';
import CustomCalendar from 'components/common/CustomCalendar';
import SearchCreateSelect, { SearchCreateOption } from 'components/common/SearchCreateSelect';
import SuggestedMember from 'components/common/SuggestedMember';
import MemberTagList from 'components/common/MemberTagList';
import SuggestedUserListShimmer from 'components/Loaders/SuggestedUserListShimmer';
import UserAvatar from 'components/common/UserAvatar';
import { useAppSelector } from 'store';
import { selectAuthUser } from 'store/slices/auth/auth.selector';
import {
  useContactOwners,
  useContactsQuery,
  useCompaniesInfinite,
  useCrmValueOptions,
} from 'service/crm';
import { useDebounce } from 'hooks/useDebounce';
import { useDealLinkScope } from 'hooks/useDealLinkScope';
import { Contact, ContactOwner } from 'types/crm/contacts';
import { Company } from 'types/crm/company';
import { UserIcon, FlagIcon, ContactAssociationIcon, CompanyAssociationIcon } from 'assets/Icons';
import { DealOption, DealFormValues, DealAssociations, DealLinkScope, PIPELINE_OPTIONS, CURRENCY_OPTIONS, DEAL_TYPE_OPTIONS, PRIORITY_OPTIONS, DEAL_PRIORITY_TEXT, DESCRIPTION_MAX } from 'types/crm/deal';
import { dealsValidationSchema } from 'utils/validation';
import { formatCloseLabel, formatDealAmount, formatDateKey } from 'utils/helpers';
import { LoaderButton } from 'components/common/LoadingButton';
import FormTextarea from 'components/common/FormTextArea';
import RequiredMark from 'components/common/RequiredMark';


interface CreateDealModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit?: (values: DealFormValues, associations: DealAssociations) => Promise<void> | void;
  isSaving?: boolean;
  editingData?: Partial<DealFormValues> | null;
  defaultValues?: Partial<DealFormValues> | null;
  /** Entity that opened the modal — drives the "Add existing" tab and the self-exclusions. */
  scope?: DealLinkScope;
  /** Display info for `scope`, shown as a locked (non-removable) pre-selected chip in the Associations section. */
  scopeEntity?: { name: string; subtitle?: string; imageUrl?: string | null };
}


const CreateDealModal: React.FC<CreateDealModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  isSaving = false,
  editingData,
  defaultValues,
  scope,
  scopeEntity,
}) => {
  const authUser = useAppSelector(selectAuthUser);
  const { data: owners = [], isLoading: ownersLoading } = useContactOwners(authUser?.orgUuid as string);

  // The organisation's configured stages (Settings → CRM Values), Active only,
  // from GET /crm/values/deal-stage/assignable — not a list compiled in.
  const { options: dealStageOptions, isLoading: isDealStageLoading } =
    useCrmValueOptions('DEAL_STAGE');

  const [selectedTab, setSelectedTab] = useState<'new' | 'existing'>('new');
  const activeTab = scope ? selectedTab : 'new';
  const setActiveTab = setSelectedTab;
  const [dealSearch, setDealSearch] = useState('');

  // --- Associations: kept in local state until the deal is created ---------
  const [selectedContacts, setSelectedContacts] = useState<SearchCreateOption[]>([]);
  const [selectedCompanies, setSelectedCompanies] = useState<SearchCreateOption[]>([]);
  const [contactSearch, setContactSearch] = useState('');
  const [companySearch, setCompanySearch] = useState('');

  const associationsEnabled = isOpen && activeTab === 'new';

  const {
    contacts,
    isLoading: contactsSearchLoading,
    isFetchingMore: contactsFetchingMore,
    hasMore: contactsHasMore,
    onLoadMore: loadMoreContacts,
  } = useContactsQuery(
    { search: contactSearch || undefined, size: 20 },
    { enabled: associationsEnabled },
  );

  const {
    companies: companyResults,
    isLoading: companiesSearchLoading,
    isFetchingMore: companiesFetchingMore,
    hasMore: companiesHasMore,
    onLoadMore: loadMoreCompanies,
  } = useCompaniesInfinite(
    authUser?.orgUuid as string,
    { search: companySearch || undefined, size: 20 },
    { enabled: associationsEnabled },
  );

  // The entity that opened the modal is already implied by the association.
  const scopedContactUuid = scope?.type === 'contact' ? scope.uuid : undefined;
  const scopedCompanyUuid = scope?.type === 'company' ? scope.uuid : undefined;

  const contactOptions = useMemo<SearchCreateOption[]>(() => {
    const excluded = new Set(
      [scopedContactUuid, ...selectedContacts.map((c) => c.uuid)].filter(Boolean) as string[],
    );
    return (contacts ?? [])
      .filter((c: Contact) => !excluded.has(c.uuid))
      .map((c: Contact) => ({
        uuid: c.uuid,
        name: c.fullName || [c.firstName, c.lastName].filter(Boolean).join(' '),
        subtitle: [c.jobTitle, c.companyName].filter(Boolean).join(' · ') || c.email,
        imageUrl: c.profileImageUrl || null
      }));
  }, [contacts, selectedContacts, scopedContactUuid]);

  const companyOptions = useMemo<SearchCreateOption[]>(() => {
    const excluded = new Set(
      [scopedCompanyUuid, ...selectedCompanies.map((c) => c.uuid)].filter(Boolean) as string[],
    );
    return companyResults
      .filter((c: Company) => !excluded.has(c.uuid))
      .map((c: Company) => ({
        uuid: c.uuid,
        name: c.name,
        imageUrl: c.imageUrl || null,
        subtitle: [c.industry, c.cityLocation].filter(Boolean).join(' · '),
      }));
  }, [companyResults, selectedCompanies, scopedCompanyUuid]);

  const addContact = (option: SearchCreateOption) => {
    setSelectedContacts((prev) => (prev.some((c) => c.uuid === option.uuid) ? prev : [...prev, option]));
    setContactSearch('');
  };

  const addCompany = (option: SearchCreateOption) => {
    setSelectedCompanies((prev) => (prev.some((c) => c.uuid === option.uuid) ? prev : [...prev, option]));
    setCompanySearch('');
  };

  const removeContact = (uuid: string) =>
    setSelectedContacts((prev) => prev.filter((c) => c.uuid !== uuid));
  const removeCompany = (uuid: string) =>
    setSelectedCompanies((prev) => prev.filter((c) => c.uuid !== uuid));

  // The entity that opened the modal is always associated on submit (see the
  // parent's onSubmit handler), so it's shown as a locked, non-removable chip
  // rather than living in `selectedContacts`/`selectedCompanies`.
  const lockedContactTag =
    scope?.type === 'contact' && scopeEntity
      ? { id: scope.uuid, name: scopeEntity.name, imageUrl: scopeEntity.imageUrl, locked: true }
      : null;
  const lockedCompanyTag =
    scope?.type === 'company' && scopeEntity
      ? { id: scope.uuid, name: scopeEntity.name, imageUrl: scopeEntity.imageUrl, locked: true }
      : null;

  // reset the whole local association selection whenever the modal closes
  useEffect(() => {
    if (!isOpen) {
      setSelectedContacts([]);
      setSelectedCompanies([]);
      setContactSearch('');
      setCompanySearch('');
      setDealSearch('');
      setActiveTab('new');
    }
  }, [isOpen]);

  const debouncedDealSearch = useDebounce(dealSearch.trim(), 400);

  // The "Add existing" tab is scoped to whichever entity opened the modal.
  const {
    deals: existingDeals,
    isLoading: dealsLoading,
    isFetchingMore: dealsFetchingMore,
    hasMore: dealsHasMore,
    onLoadMore: loadMoreDeals,
    toggleLink: toggleLinkDeal,
    isTogglePending: isLinkTogglePending,
  } = useDealLinkScope(
    scope,
    debouncedDealSearch,
    isOpen && activeTab === 'existing',
  );

  // scroll-based pagination for the "Add existing" deal list
  const dealsListRef = useRef<HTMLDivElement | null>(null);
  const dealsSentinelRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (activeTab !== 'existing' || !dealsHasMore || dealsLoading || dealsFetchingMore) return;
    const node = dealsSentinelRef.current;
    const root = dealsListRef.current;
    if (!node || !root) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          loadMoreDeals();
        }
      },
      { root, threshold: 0.1 },
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [
    activeTab,
    dealsHasMore,
    dealsLoading,
    dealsFetchingMore,
    loadMoreDeals,
    existingDeals.length,
  ]);

  const isDealLinked = (deal: DealOption) => deal.associated;

  const ownerOptions = owners.map((o: ContactOwner) => ({
    value: o.uuid,
    label: `${o.name}`.trim(),
  }));

  const seed = editingData ?? defaultValues;

  const initialValues: DealFormValues = {
    name: seed?.name || '',
    // No default: the user picks a pipeline themselves, as on the Create Deal page.
    pipeline: seed?.pipeline || '',
    dealStage: seed?.dealStage || '',
    currency: seed?.currency || 'INR',
    amount: seed?.amount || '',
    closeDate: seed?.closeDate || '',
    owner: seed?.owner || (editingData ? '' : authUser?.appUserUuid ?? ''),
    dealType: seed?.dealType || '',
    priority: seed?.priority || '',
    description: seed?.description || '',
  };

  return createPortal(
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-[9999]" onClose={onClose}>
        <TransitionChild
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" />
        </TransitionChild>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <TransitionChild
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <DialogPanel className="flex flex-col w-full max-w-[800px] h-[81vh] transform overflow-hidden rounded-2xl bg-[#131720] border border-[#232736] text-left align-middle shadow-2xl transition-all relative">
                {/* Header */}
                <div className="flex items-center shrink-0 justify-between px-7 py-5 border-b border-primary-subtle">
                  <DialogTitle as="h3" className="text-neutral text-[20px] font-bold tracking-wide">
                    {editingData ? 'Edit Deal' : 'Create Deal'}
                  </DialogTitle>
                  <button
                    type="button"
                    className="flex items-center justify-center w-8 h-8 rounded-lg border border-[#232736] text-neutral-medium hover:text-neutral hover:border-primary-subtle transition-colors focus:outline-none"
                    onClick={onClose}
                  >
                    <X size={18} />
                  </button>
                </div>

                {scope && (
                  <div className="flex items-center shrink-0 gap-3 px-7 border-b border-primary-subtle">
                    {([
                      { key: 'existing', label: 'Add existing' },
                      { key: 'new', label: 'Create new' },
                    ] as const).map((tab) => (
                      <button
                        key={tab.key}
                        type="button"
                        onClick={() => setActiveTab(tab.key)}
                        className={`relative py-3.5 text-sm font-semibold transition-colors px-2 ${activeTab === tab.key
                          ? 'text-neutral'
                          : 'text-neutral-medium hover:text-neutral'
                          }`}
                      >
                        {tab.label}
                        {activeTab === tab.key && (
                          <span className="absolute inset-x-0 -bottom-px h-0.5 rounded-full bg-gradient-to-r from-[#6366f1] to-[#8b5cf6]" />
                        )}
                      </button>
                    ))}
                  </div>
                )}

                {activeTab === 'new' ? (
                  <Formik
                    initialValues={initialValues}
                    enableReinitialize
                    validationSchema={dealsValidationSchema}
                    onSubmit={async (values, { setSubmitting }) => {
                      try {
                        await onSubmit?.(values, {
                          contactUuids: selectedContacts.map((c) => c.uuid),
                          companyUuids: selectedCompanies.map((c) => c.uuid),
                        });
                      } finally {
                        setSubmitting(false);
                      }
                    }}
                  >
                    {({
                      values,
                      errors,
                      touched,
                      handleBlur,
                      handleSubmit,
                      getFieldProps,
                      setFieldValue,
                      dirty,
                      isValid
                    }: FormikProps<DealFormValues>) => (
                      <Form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
                        <div className="px-7 pt-8 pb-6 space-y-5 flex-1 min-h-0 overflow-y-auto">
                          {/* Deal Name + Pipeline */}
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                            <FormInput
                              {...getFieldProps('name')}
                              label="Deal Name"
                              required
                              placeholder="Enter deal name"
                              error={touched.name && errors.name ? (errors.name as string) : undefined}
                            />
                            <CustomSelect
                              label="Pipeline"
                              name="pipeline"
                              options={PIPELINE_OPTIONS}
                              placeholder="Select pipeline"
                              required
                            />
                          </div>

                          {/* Deal Stage + Amount */}
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                            <CustomSelect
                              label="Deal Stage"
                              name="dealStage"
                              options={dealStageOptions}
                              disabled={isDealStageLoading}
                              placeholder={isDealStageLoading ? 'Loading stages...' : 'Select deal stage'}
                              required
                            />
                            <div>
                              <label className="block text-[14px] font-normal text-[#9CA3AF] mb-[11px] leading-[100%]">
                                Amount<RequiredMark />
                              </label>
                              <div className="flex items-center gap-2">
                                <CustomSelect
                                  name="currency"
                                  options={CURRENCY_OPTIONS}
                                  className="w-[90px] shrink-0"
                                />
                                <div className="flex-1">
                                  <FormInput
                                    {...getFieldProps('amount')}
                                    name="amount"
                                    type="number"
                                    placeholder="Enter amount"
                                    error={touched.amount && errors.amount ? (errors.amount as string) : undefined}
                                  />
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Close Date + Deal Owner */}
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                            <CustomCalendar
                              label="Close Date"
                              placeholder="Select close date"
                              value={values.closeDate}
                              minDate={new Date()}
                              onChange={(date) => setFieldValue('closeDate', formatDateKey(date))}
                            />
                            <CustomSelect
                              label="Deal Owner"
                              name="owner"
                              options={ownerOptions}
                              placeholder={ownersLoading ? 'Loading owners...' : 'Select deal owner'}
                              className={{ value: '!pl-7' }}
                              icon={
                                values.owner ? (
                                  <UserAvatar
                                    name={
                                      ownerOptions.find((o) => o.value === values.owner)?.label ?? ''
                                    }
                                    className="w-6 h-6 text-[10px] mr-[40px]"
                                  />
                                ) : (
                                  <UserIcon size={18} />
                                )
                              }
                            />
                          </div>

                          {/* Deal Type + Priority */}
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                            <CustomSelect
                              label="Deal Type"
                              name="dealType"
                              options={DEAL_TYPE_OPTIONS}
                              placeholder="Select deal type"
                            />
                            <CustomSelect
                              label="Priority"
                              name="priority"
                              options={PRIORITY_OPTIONS}
                              placeholder="Select priority"
                              icon={<FlagIcon />}
                            />
                          </div>

                          {/* Associations */}
                          <div >
                            <p className="mb-3 text-sm  tracking-wide text-neutral-medium">
                              Associations
                            </p>
                            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                              {/* Contacts */}
                              <div className="rounded-2xl border border-primary-subtle bg-primary-intemediate/40 p-4">
                                <div className="mb-4 flex items-center gap-3">
                                  <ContactAssociationIcon size={40} />
                                  <div>
                                    <h4 className="text-[15px] font-semibold text-neutral">Contacts</h4>
                                    <p className="text-xs text-neutral-medium">Link contacts to this deal.</p>
                                  </div>
                                </div>
                                <p className="mb-2 text-[13px] text-neutral-medium">Associate Contacts</p>
                                <MemberTagList
                                  className="mb-3"
                                  items={[
                                    ...(lockedContactTag ? [lockedContactTag] : []),
                                    ...selectedContacts.map((contact) => ({
                                      id: contact.uuid,
                                      name: contact.name,
                                      imageUrl: contact.imageUrl,
                                    })),
                                  ]}
                                  onRemove={removeContact}
                                />
                                <SearchCreateSelect
                                  placeholder="Search contacts..."
                                  listHeading="Contacts"
                                  emptyLabel="No contacts found"
                                  icon={User}
                                  hideCreateButton
                                  showOptionAddAction
                                  clearOnSelect
                                  value={contactSearch}
                                  options={contactOptions}
                                  isLoading={contactsSearchLoading}
                                  isFetchingMore={contactsFetchingMore}
                                  hasMore={contactsHasMore}
                                  onLoadMore={loadMoreContacts}
                                  loadingContent={<SuggestedUserListShimmer />}
                                  renderOption={(option, select) => (
                                    <SuggestedMember
                                      item={{ id: option.uuid, name: option.name, subtitle: option.subtitle, imageUrl: option.imageUrl }}
                                      isSelected={false}
                                      onSelect={select}
                                    />
                                  )}
                                  onSearchChange={setContactSearch}
                                  onSelectOption={addContact}
                                />
                              </div>

                              {/* Companies */}
                              <div className="rounded-2xl border border-primary-subtle bg-primary-intemediate/40 p-4">
                                <div className="mb-4 flex items-center gap-3">
                                  <CompanyAssociationIcon size={40} />
                                  <div>
                                    <h4 className="text-[15px] font-semibold text-neutral">Companies</h4>
                                    <p className="text-xs text-neutral-medium">Link companies to this deal.</p>
                                  </div>
                                </div>
                                <p className="mb-2 text-[13px] text-neutral-medium">Associate Companies</p>
                                <MemberTagList
                                  className="mb-3 "
                                  items={[
                                    ...(lockedCompanyTag ? [lockedCompanyTag] : []),
                                    ...selectedCompanies.map((company) => ({
                                      id: company.uuid,
                                      name: company.name,
                                      imageUrl: company.imageUrl,
                                    })),
                                  ]}
                                  onRemove={removeCompany}
                                  renderLeading={() => (
                                    <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary-variant">
                                      <Building2 size={13} className="text-neutral-medium" />
                                    </span>
                                  )}
                                />
                                <SearchCreateSelect
                                  placeholder="Search companies..."
                                  listHeading="Companies"
                                  emptyLabel="No companies found"
                                  icon={Building2}
                                  hideCreateButton
                                  showOptionAddAction
                                  clearOnSelect
                                  value={companySearch}
                                  options={companyOptions}
                                  isLoading={companiesSearchLoading}
                                  isFetchingMore={companiesFetchingMore}
                                  hasMore={companiesHasMore}
                                  onLoadMore={loadMoreCompanies}
                                  loadingContent={<SuggestedUserListShimmer />}
                                  renderOption={(option, select) => (
                                    <SuggestedMember
                                      item={{ id: option.uuid, name: option.name, subtitle: option.subtitle, imageUrl: option.imageUrl }}
                                      isSelected={false}
                                      onSelect={select}
                                      leading={
                                        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary-variant">
                                          <Building2 size={15} className="text-neutral-medium" />
                                        </span>
                                      }
                                    />
                                  )}
                                  onSearchChange={setCompanySearch}
                                  onSelectOption={addCompany}
                                />
                              </div>
                            </div>
                          </div>

                          {/* Description */}
                          {/* <div>
                            <label
                              htmlFor="description"
                              className="block text-[14px] font-normal text-[#9CA3AF] mb-[11px] leading-[100%]"
                            >
                              Description
                            </label>
                            <textarea
                              {...getFieldProps('description')}
                              id="description"
                              rows={4}
                              maxLength={DESCRIPTION_MAX}
                              placeholder="Add deal description, notes or any additional information..."
                              className="w-full bg-primary-intemediate border border-primary-subtle rounded-lg px-4 py-3 text-neutral placeholder:text-[#374151] text-sm focus:outline-none focus:border-blue-500/50 resize-none transition-colors"
                            />
                            <div className="mt-1 flex items-center justify-between">
                              {touched.description && errors.description ? (
                                <span className="text-red-500 text-xs">{errors.description as string}</span>
                              ) : (
                                <span />
                              )}
                              <span className="text-xs text-neutral-medium">
                                {values.description.length}/{DESCRIPTION_MAX}
                              </span>
                            </div>
                          </div> */}
                          <FormTextarea
                            name="description"
                            label="Insights"
                            placeholder="Add insights about this deal"
                            maxLength={500}
                            darkBg
                            className={{ label: 'text-sm', textarea: 'text-sm' }}
                          />

                        </div>

                        {/* Footer */}
                        <div className="flex items-center justify-end gap-3 border-t border-primary-subtle pt-5 px-7 pb-6 shrink-0">
                          <Button
                            type="button"
                            onClick={onClose}
                            disabled={isSaving}
                            className="py-3 px-5 rounded-lg border text-sm border-primary-subtle"
                          >
                            Cancel
                          </Button>
                          <LoaderButton
                            type="submit"
                            className="!mt-0"
                            text={editingData ? 'Save Changes' : 'Create Deal'}
                            isLoading={isSaving}
                            disabled={!isValid || !dirty}
                          />
                        </div>
                      </Form>
                    )}
                  </Formik>
                ) : (
                  <div className="px-7 pt-8 pb-6 flex-1 min-h-0 flex flex-col">
                    <label className="block text-[14px] font-normal text-[#9CA3AF] mb-[11px] leading-[100%] shrink-0">
                      Search existing deals
                    </label>
                    <div className="relative shrink-0">
                      <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-neutral-medium" />
                      <input
                        type="text"
                        value={dealSearch}
                        onChange={(e) => setDealSearch(e.target.value)}
                        placeholder="Search by deal name"
                        className="w-full bg-primary-subtle border border-primary-subtle rounded-lg pl-10 pr-4 h-[46px] text-neutral placeholder:text-neutral-medium text-sm focus:outline-none focus:border-accent transition-colors"
                      />
                    </div>

                    {/* Deal results */}
                    <div
                      ref={dealsListRef}
                      className="mt-5 space-y-3 flex-1 min-h-0 overflow-y-auto"
                    >
                      {dealsLoading && (
                        <p className="py-6 text-center text-sm text-neutral-medium">
                          Loading deals...
                        </p>
                      )}
                      {!dealsLoading && existingDeals.map((deal: DealOption) => {
                        const isLinked = isDealLinked(deal);
                        const isPending = isLinkTogglePending(deal.uuid);
                        return (
                          <div
                            key={deal.uuid}
                            className="flex items-center justify-between gap-4 bg-primary-intemediate border border-primary-subtle rounded-xl px-5 py-4"
                          >
                            <div className="min-w-0 text-left">
                              <p className="text-neutral text-[15px] font-bold truncate">
                                {deal.name}
                              </p>
                              <p className="mt-1 text-sm text-neutral-medium truncate">
                                {deal.dealStageLabel || '—'} ·{' '}
                                {deal.amount != null
                                  ? formatDealAmount(deal.amount, deal.currency ?? 'INR')
                                  : '—'}{' '}
                                · closes {formatCloseLabel(deal.closeDate)}
                              </p>
                            </div>
                            <button
                              type="button"
                              onClick={() => toggleLinkDeal(deal)}
                              disabled={isPending}
                              className={`shrink-0 w-[104px] h-[38px] inline-flex items-center justify-center rounded-lg border text-sm font-semibold transition-colors ${isLinked
                                ? 'bg-primary-light border-emphasis text-emphasis'
                                : 'bg-primary-light border-primary-subtle text-neutral hover:border-neutral-medium'
                                }`}
                            >
                              {isLinked ? 'Unlink' : 'Link'}
                            </button>
                          </div>
                        );
                      })}
                      {!dealsLoading && dealsFetchingMore && (
                        <p className="py-3 text-center text-sm text-neutral-medium">
                          Loading more deals...
                        </p>
                      )}
                      {/* invisible sentinel that pulls the next page when scrolled into view */}
                      {!dealsLoading && dealsHasMore && (
                        <div ref={dealsSentinelRef} className="h-1 w-full" />
                      )}
                      {!dealsLoading && existingDeals.length === 0 && (
                        <p className="py-6 text-center text-sm text-neutral-medium">
                          {dealSearch.trim()
                            ? 'No deals found matching your search.'
                            : 'No existing deals yet.'}
                        </p>
                      )}
                    </div>

                    {/* <ModalFooter
                      onCancel={onClose}
                      primaryText="Done"
                      primaryType="button"
                      onPrimary={onClose}
                      className="mt-8"
                    /> */}
                  </div>
                )}
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </Transition>,
    document.body
  );
};

export default CreateDealModal;
